import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Brand } from '../components/Brand.js';
import { DemoMap } from '../components/DemoMap.js';
import { RouteGeometryPreview } from '../components/RouteGeometryPreview.js';
import { RouteMethodNote } from '../components/RouteMethodNote.js';
import { demoStages, demoTripDistanceKm } from '../data/demoTrip.js';

type DemoCheer = {
  name: string;
  initials: string;
  time: string;
  body: string;
};

const demoCheersByStage: Record<string, DemoCheer[]> = {
  'augsburg-landsberg': [
    {
      name: 'Lena',
      initials: 'L',
      time: '2h ago',
      body: 'First day done. Go eat something.',
    },
    {
      name: 'Dad',
      initials: 'D',
      time: '2h ago',
      body: 'We found Landsberg on the map. Nice work!',
    },
  ],
  'landsberg-fuessen': [
    {
      name: 'Sam',
      initials: 'S',
      time: 'Yesterday',
      body: 'That lake section looked brilliant.',
    },
    {
      name: 'Mum',
      initials: 'M',
      time: 'Yesterday',
      body: 'Füssen already? Sleep well.',
    },
  ],
  'fuessen-imst': [
    {
      name: 'Jonas',
      initials: 'J',
      time: '6h ago',
      body: 'Imst! Bet your legs know about it.',
    },
    {
      name: 'Lena',
      initials: 'L',
      time: '5h ago',
      body: 'Huge day. Proud of you.',
    },
  ],
  'imst-reschen': [
    {
      name: 'Mum',
      initials: 'M',
      time: '12s ago',
      body: 'You made the pass! We are all cheering from the kitchen 💛',
    },
    {
      name: 'Jonas',
      initials: 'J',
      time: '1m ago',
      body: 'Nearly there. Keep turning those pedals.',
    },
  ],
};

const frictionBenefits = [
  {
    className: 'is-coral',
    icon: '↗',
    prompt: '“Where are you now?”',
    title: 'One link shows the trip.',
    copy: 'Friends open the same browser link all trip long. No app, account, or daily link hunt.',
  },
  {
    className: 'is-lime',
    icon: '≋',
    prompt: '“The signal is gone.”',
    title: 'Recording continues offline.',
    copy: 'The phone holds accepted GPS points locally and catches up when the network comes back.',
  },
  {
    className: 'is-sky',
    icon: '04',
    prompt: '“Which day was that?”',
    title: 'Each stage stays clear.',
    copy: 'Daily maps and stats stay distinct, while the whole journey grows into one lasting record.',
  },
];

const faqs = [
  [
    'What can I do for free?',
    'Plan private trips, organize daily stages, record rides, and keep your maps and statistics together. A membership is only needed when you want to publish a live follower link.',
  ],
  [
    'Do followers need the app?',
    'No. They open your private trip link in any modern browser, without an account or payment.',
  ],
  [
    'What if I lose signal in the mountains?',
    'The native app keeps accepted route points in its on-phone queue. It synchronizes when a connection returns; followers see the last truthful update meanwhile.',
  ],
  [
    'How do I control live sharing?',
    'Protect the link with a password, replace it if it travels too far, or stop sharing at any time. Active links publish live without an artificial delay.',
  ],
  [
    'Can people write back to me?',
    'On the Unlimited plan, the cyclist can enable viewer comments for the shared journey and turn them off again at any time.',
  ],
  [
    'Is this an emergency tracker?',
    'No. Mobile networks, GPS, batteries, and browsers can fail. Never use Bikepacking Live as your only safety or emergency system.',
  ],
];

function formatRideTime(hours: number) {
  const totalMinutes = Math.round(hours * 60);
  const rideHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${rideHours}h ${String(minutes).padStart(2, '0')}m`;
}

export function LandingPage() {
  const [selectedJourneyStageId, setSelectedJourneyStageId] = useState(
    demoStages.at(-1)?.id ?? demoStages[0]?.id ?? '',
  );
  const [demoCheerDraft, setDemoCheerDraft] = useState('');
  const [sentDemoCheers, setSentDemoCheers] = useState<Record<string, string>>({});
  const selectedJourneyStageIndex = Math.max(
    0,
    demoStages.findIndex((stage) => stage.id === selectedJourneyStageId),
  );
  const selectedJourneyStage = demoStages[selectedJourneyStageIndex]!;
  const cumulativeDistanceKm = demoStages
    .slice(0, selectedJourneyStageIndex + 1)
    .reduce((total, stage) => total + stage.distanceKm, 0);
  const journeyProgress = Math.min(100, (cumulativeDistanceKm / demoTripDistanceKm) * 100);
  const selectedStageCheers = demoCheersByStage[selectedJourneyStage.id] ?? [];
  const sentDemoCheer = sentDemoCheers[selectedJourneyStage.id];

  function sendDemoCheer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const body = demoCheerDraft.trim();
    if (!body) return;
    setSentDemoCheers((current) => ({ ...current, [selectedJourneyStage.id]: body }));
    setDemoCheerDraft('');
  }

  return (
    <div className="marketing-shell retro-landing">
      <header className="site-header wrap">
        <Brand />
        <nav aria-label="Main navigation">
          <a href="#benefits">Why it clicks</a>
          <a href="#playground">Try the demo</a>
          <a href="#pricing">Pricing</a>
        </nav>
        <div className="header-actions">
          <Link to="/signin">Sign in</Link>
          <Link className="button button--small" to="/start">
            Plan a ride
          </Link>
        </div>
      </header>

      <main>
        <section className="hero wrap" id="demo">
          <div className="hero-copy">
            <p className="eyebrow">Private trip planning · live when you want it</p>
            <h1>
              Plan your bikepacking trip.
              <br />
              Share the ride live.
            </h1>
            <p className="lede">
              Plan every riding day privately for free, then add a membership so friends and family
              can follow one live link instead of asking where you are.
            </p>
            <div className="button-row">
              <Link className="button" to="/start">
                Plan a trip for free
              </Link>
              <Link className="button button--quiet" to="/t/demo-via-claudia">
                See the live demo
              </Link>
            </div>
            <ul className="hero-proof" aria-label="Product highlights">
              <li>Free private planning</li>
              <li>Live with membership</li>
              <li>Followers need no app</li>
            </ul>
          </div>
          <div className="hero-map-stack">
            <DemoMap />
            <span className="map-note" aria-hidden="true">
              Augsburg → the Alps
            </span>
            <aside className="hero-live-moment" aria-label="Live viewer activity">
              <div className="hero-watch-count">
                <i /> 18 watching live
              </div>
              <div className="hero-cheer">
                <span aria-hidden="true">M</span>
                <p>
                  <small>
                    <strong>Mum</strong> · 12s ago
                  </small>
                  You made the pass! We are all cheering from the kitchen 💛
                </p>
              </div>
            </aside>
          </div>
        </section>

        <div className="retro-marquee" aria-hidden="true">
          <div>
            <span>PLAN FOR FREE</span>
            <i>✦</i>
            <span>EVERY RIDING DAY</span>
            <i>✦</i>
            <span>WEAK SIGNAL FRIENDLY</span>
            <i>✦</i>
            <span>FOLLOWERS NEED NO APP</span>
            <i>✦</i>
            <span>PLAN FOR FREE</span>
            <i>✦</i>
            <span>EVERY RIDING DAY</span>
            <i>✦</i>
            <span>WEAK SIGNAL FRIENDLY</span>
            <i>✦</i>
            <span>FOLLOWERS NEED NO APP</span>
            <i>✦</i>
          </div>
        </div>

        <section className="section wrap benefit-intro" id="benefits">
          <div className="benefit-heading">
            <p className="eyebrow">Useful before the wheels even move</p>
            <h2>Plan privately. Share when ready.</h2>
            <p>
              Build the route into clear riding days and keep everything organized for yourself.
              When the trip starts, live sharing turns the repeated “where are you?” messages into
              one easy place to look.
            </p>
          </div>
          <div className="friction-grid">
            {frictionBenefits.map((benefit) => (
              <article className={benefit.className} key={benefit.title}>
                <span className="benefit-icon" aria-hidden="true">
                  {benefit.icon}
                </span>
                <p>{benefit.prompt}</p>
                <h3>{benefit.title}</h3>
                <div />
                <small>{benefit.copy}</small>
              </article>
            ))}
          </div>
        </section>

        <section className="section retro-demo" id="playground">
          <div className="wrap">
            <header className="retro-demo-heading">
              <div>
                <p className="eyebrow">A real route, not a render</p>
                <h2>Explore a four-day ride.</h2>
              </div>
              <p>
                Pick a day. The map zooms to the actual bicycle route, the daily numbers change, and
                the whole-trip total keeps rolling forward. It is exactly what a follower sees.
              </p>
            </header>

            <div className="demo-playground">
              <div className="retro-demo-map">
                <DemoMap
                  compact
                  label="REAL VIA CLAUDIA ROUTE"
                  selectedStageId={selectedJourneyStage.id}
                />
                <span className="route-scribble" aria-hidden="true">
                  pick a day ↘
                </span>
              </div>

              <div className="demo-control-panel">
                <header>
                  <span>Via Claudia · 4-day sample</span>
                  <strong>Augsburg → Reschen Pass</strong>
                </header>
                <div className="retro-stage-tabs" aria-label="Choose a sample riding day">
                  {demoStages.map((stage) => (
                    <button
                      aria-pressed={stage.id === selectedJourneyStage.id}
                      className={stage.id === selectedJourneyStage.id ? 'is-selected' : ''}
                      key={stage.id}
                      type="button"
                      onClick={() => setSelectedJourneyStageId(stage.id)}
                    >
                      <span>DAY {String(stage.number).padStart(2, '0')}</span>
                      <strong>{stage.to}</strong>
                      <small>{stage.distanceKm.toFixed(1)} km</small>
                    </button>
                  ))}
                </div>

                <article className="retro-stage-readout" aria-live="polite">
                  <div className="stage-readout-status">
                    <span>Day {String(selectedJourneyStage.number).padStart(2, '0')}</span>
                    <b className={selectedJourneyStage.state === 'live' ? 'is-live' : ''}>
                      <i /> {selectedJourneyStage.state === 'live' ? 'Live now' : 'Completed'}
                    </b>
                  </div>
                  <h3>{selectedJourneyStage.title}</h3>
                  <p>
                    {selectedJourneyStage.from} <span>→</span> {selectedJourneyStage.to}
                  </p>
                  <dl>
                    <div>
                      <dt>Distance</dt>
                      <dd>{selectedJourneyStage.distanceKm.toFixed(1)} km</dd>
                    </div>
                    <div>
                      <dt>Ride time</dt>
                      <dd>{formatRideTime(selectedJourneyStage.durationHours)}</dd>
                    </div>
                    <div>
                      <dt>Average</dt>
                      <dd>
                        {(
                          selectedJourneyStage.distanceKm / selectedJourneyStage.durationHours
                        ).toFixed(1)}{' '}
                        km/h
                      </dd>
                    </div>
                  </dl>
                  <div className="retro-rollup">
                    <p>
                      <span>The whole journey after this day</span>
                      <strong>{cumulativeDistanceKm.toFixed(1)} km</strong>
                    </p>
                    <div>
                      <i style={{ width: `${journeyProgress}%` }} />
                    </div>
                  </div>
                  <Link to="/t/demo-via-claudia">Open the full follower view →</Link>
                </article>
              </div>

              <section className="demo-cheers" aria-labelledby="demo-cheers-title">
                <header>
                  <span>Unlimited feature</span>
                  <h3 id="demo-cheers-title">Cheers from home</h3>
                  <p>People can leave a comment on the same page they use to follow the ride.</p>
                </header>
                <div className="demo-cheer-area">
                  <div className="demo-cheer-list" aria-live="polite" key={selectedJourneyStage.id}>
                    {[
                      ...selectedStageCheers,
                      ...(sentDemoCheer
                        ? [
                            {
                              name: 'You',
                              initials: 'Y',
                              time: 'Now',
                              body: sentDemoCheer,
                            },
                          ]
                        : []),
                    ].map((cheer) => (
                      <article key={`${selectedJourneyStage.id}-${cheer.name}-${cheer.body}`}>
                        <span aria-hidden="true">{cheer.initials}</span>
                        <div>
                          <p>
                            <strong>{cheer.name}</strong>
                            <time>{cheer.time}</time>
                          </p>
                          <blockquote>{cheer.body}</blockquote>
                        </div>
                      </article>
                    ))}
                  </div>
                  <form className="demo-cheer-form" onSubmit={sendDemoCheer}>
                    <label htmlFor="demo-cheer">Try sending a cheer</label>
                    <div>
                      <input
                        id="demo-cheer"
                        maxLength={120}
                        placeholder="Type something encouraging"
                        value={demoCheerDraft}
                        onChange={(event) => setDemoCheerDraft(event.target.value)}
                      />
                      <button type="submit" disabled={!demoCheerDraft.trim()}>
                        Send
                      </button>
                    </div>
                    <small>This is just a demo. Nothing gets saved.</small>
                  </form>
                </div>
              </section>
            </div>

            <ul className="demo-payoff" aria-label="Follower-view benefits">
              <li>
                <strong>One tap for followers</strong>
                <span>The link opens straight in their browser.</span>
              </li>
              <li>
                <strong>Daily + total stats</strong>
                <span>Each stage matters without losing the big picture.</span>
              </li>
              <li>
                <strong>The truthful last update</strong>
                <span>No fake movement when the rider is offline.</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="section wrap">
          <RouteMethodNote />
        </section>

        <section className="section wrap benefit-bento">
          <header>
            <p className="eyebrow">One home for the entire journey</p>
            <h2>Plan each stage. Share the trip when ready.</h2>
          </header>
          <div className="bento-grid">
            <article className="bento-card bento-card--planning">
              <span className="bento-kicker">Plan before you pedal</span>
              <h3>Choose two points. Build the route.</h3>
              <p>
                Build a bicycle route on the map or bring your own GPX. Planning stays free and
                private until you decide to share the ride live.
              </p>
              <div className="route-doodle">
                <RouteGeometryPreview
                  segments={[demoStages[2]!]}
                  ariaLabel="Production-generated bicycle route from Füssen to Imst"
                />
                <small>Füssen → Imst · real route</small>
              </div>
            </article>
            <article className="bento-card bento-card--offline">
              <span className="bento-kicker">Off-grid-ish</span>
              <h3>Keep recording without signal.</h3>
              <p>The ride stays queued on the phone and syncs when reception wakes up.</p>
              <div className="signal-doodle" aria-hidden="true">
                <i />
                <i />
                <i />
                <b>47 saved points</b>
              </div>
            </article>
            <article className="bento-card bento-card--story">
              <span className="bento-kicker">The long view</span>
              <h3>Keep every stage together.</h3>
              <p>Title every stage, keep every recap, and watch the complete route take shape.</p>
              <div className="stage-stamps" aria-hidden="true">
                <span>01 ✓</span>
                <span>02 ✓</span>
                <span>03 ✓</span>
                <span>04 LIVE</span>
              </div>
            </article>
            <article className="bento-card bento-card--notes">
              <span className="bento-kicker">Unlimited plan</span>
              <h3>Let followers cheer you on.</h3>
              <p>
                Turn on viewer comments when you want encouragement, then switch them off when not.
              </p>
              <blockquote>“That climb looked wild. Go Mara!”</blockquote>
            </article>
            <article className="bento-card bento-card--browser">
              <span className="bento-kicker">No install required</span>
              <h3>Followers only need a browser.</h3>
              <p>Grandparents, group chats, and curious friends all get the same simple view.</p>
              <div className="mini-browser" aria-hidden="true">
                <i />
                <i />
                <i />
                <span>bikepackinglive.com/t/your-trip</span>
              </div>
            </article>
          </div>
        </section>

        <section className="section wrap retro-how" id="how">
          <div>
            <p className="eyebrow">Roadside simple</p>
            <h2>Plan. Record. Share live.</h2>
          </div>
          <ol>
            <li>
              <span>1</span>
              <strong>Plan the journey for free</strong>
              <p>Name the trip, add each riding day, and keep the whole plan private.</p>
            </li>
            <li>
              <span>2</span>
              <strong>Record every riding day</strong>
              <p>Track the route and keep each stage map and its statistics in one journey.</p>
            </li>
            <li>
              <span>3</span>
              <strong>Make it live when you want</strong>
              <p>
                Start a membership, switch on the follower link, and let people watch from home.
              </p>
            </li>
          </ol>
        </section>

        <section className="section faq wrap retro-faq">
          <div>
            <p className="eyebrow">Good questions</p>
            <h2>Common questions.</h2>
            <span className="faq-sticker" aria-hidden="true">
              no silly
              <br />
              questions
            </span>
          </div>
          <div>
            {faqs.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="trial-cta">
          <div className="wrap trial-cta-grid">
            <div>
              <p className="eyebrow">Your plan stays useful either way</p>
              <h2>Plan your whole trip for free.</h2>
              <p>
                Create the journey, organize each riding day, and record it privately without a
                subscription. Add live sharing only for the months when people want to follow.
              </p>
              <div className="button-row">
                <Link className="button button--light" to="/start">
                  Plan a trip for free
                </Link>
                <Link className="button button--outline-light" to="/t/demo-via-claudia">
                  Play with the demo
                </Link>
              </div>
            </div>
            <div className="trial-ticket" aria-hidden="true">
              <span>BIKEPACKING LIVE</span>
              <strong>PRIVATE TRIP PLANNER</strong>
              <p>Trips + stages + private ride records</p>
              <b>€0</b>
              <small>No card. Membership starts only when you choose live sharing.</small>
            </div>
          </div>
        </section>

        <section className="section pricing wrap retro-pricing" id="pricing">
          <div className="section-intro">
            <p className="eyebrow">Planning is free</p>
            <h2>Pay only for live sharing.</h2>
            <p>
              Keep planning and recording private trips without a subscription. Choose a monthly
              membership when you want to publish a live link.
            </p>
          </div>
          <div className="price-grid">
            <article className="is-free">
              <div>
                <span className="price-badge">Always useful</span>
                <h3>Private Planner</h3>
                <p>Plan and record for yourself</p>
              </div>
              <p className="price">
                <strong>€0</strong>
                <span>forever</span>
              </p>
              <ul>
                <li>Unlimited private trips and stages</li>
                <li>Organize every riding day</li>
                <li>Private route tracking and statistics</li>
                <li>No public live follower link</li>
              </ul>
              <Link className="button button--wide button--quiet" to="/start">
                Plan a trip for free
              </Link>
            </article>
            <article>
              <div>
                <span className="price-badge">Most bikepackers</span>
                <h3>Bikepacker</h3>
                <p>Up to 100 people watching live</p>
              </div>
              <p className="price">
                <strong>€25</strong>
                <span>/ month</span>
              </p>
              <ul>
                <li>Unlimited live journeys and stages</li>
                <li>Offline recording and later sync</li>
                <li>Daily and whole-trip statistics</li>
                <li>One stable follower link</li>
              </ul>
              <Link className="button button--wide" to="/start">
                Bring up to 100
              </Link>
            </article>
            <article className="is-unlimited">
              <div>
                <span className="price-badge">For a bigger send-off</span>
                <h3>Bikepacker Unlimited</h3>
                <p>No fixed live audience cap</p>
              </div>
              <p className="price">
                <strong>€59</strong>
                <span>/ month</span>
              </p>
              <ul>
                <li>Everything in Bikepacker</li>
                <li>No 100-person live capacity limit</li>
                <li>Optional viewer comments</li>
                <li>Made for creators and big journeys</li>
              </ul>
              <Link className="button button--wide" to="/start">
                Open the audience
              </Link>
            </article>
          </div>
          <p className="price-note">
            Monthly consumer prices include applicable VAT where required. App Store and Google Play
            show their localized price before purchase. Cancel future renewals at any time.
          </p>
        </section>
      </main>

      <footer className="site-footer wrap">
        <Brand />
        <p>Plan the long way around. Share it live when you want.</p>
        <nav>
          <Link to="/bikepacking-trip-planner/">Trip planner</Link>
          <Link to="/bikepacking-gpx-route-planner/">GPX planning</Link>
          <Link to="/bikepacking-distance-per-day/">Daily distance</Link>
          <Link to="/weekend-bikepacking-trip-planner/">Weekend trips</Link>
          <Link to="/bikepacking-app/">Bikepacking app</Link>
          <Link to="/live-bikepacking-tracker/">Live tracking</Link>
          <Link to="/share-bikepacking-trip/">Trip sharing</Link>
          <Link to="/offline-bikepacking-tracker/">Weak signal</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/imprint">Imprint</Link>
          <Link to="/delete-account">Delete account</Link>
          <a href="https://ahmed.wtf/bikepacking-live/support/">Support</a>
          <span>© {new Date().getFullYear()}</span>
        </nav>
      </footer>
    </div>
  );
}
